import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import Footer from "~/components/Footer";
import { useScrollReveal } from "~/lib/use-scroll-reveal";
import styles from "~/styles/press.module.css";

export const meta: MetaFunction = () => [
  { title: "Press — Prodigium Dynamics" },
  { name: "description", content: "Press kit, brand assets, and media contact for Prodigium Dynamics." },
];

export default function PressPage() {
  useScrollReveal();

  return (
    <>
      <section className={styles.hero}>
        <p className={styles.heroLabel}>Press Kit</p>
        <h1 className={styles.heroTitle}>PRESS &<br />MEDIA.</h1>
        <p className={styles.heroSub}>
          Everything you need to write about us. Brand assets, product information,
          and founding context — all in one place.
        </p>
      </section>

      <div className="rule" />

      <section className={styles.section}>
        <p className={styles.sectionLabel}>Company Overview</p>
        <div className={styles.boilerplate}>
          <p className={styles.boilerplateTitle}>BOILERPLATE</p>
          <p className={styles.boilerplateText}>
            Prodigium Dynamics is an AI-driven studio building digital products, content systems, and connection layers
            for a multilingual world. Founded in 2026, the studio operates quietly — building deliberately and releasing
            only what deserves to exist. Current products include PageBurn (reading technology), Skills (AI system prompts),
            and Brubri (execution system for neurodivergent users).
          </p>
          <div className={styles.boilerplateMeta}>
            <span>Founded: 2026</span>
            <span>Operations: Global</span>
            <span>Contact: global@prodigiumdynamics.space</span>
          </div>
        </div>
      </section>

      <div className="rule" />

      <section className={styles.section}>
        <p className={styles.sectionLabel}>Products</p>
        <div className={styles.productGrid}>
          {[
            { name: "PAGEBURN", tag: "Reading Technology", status: "Live", desc: "A reading environment built around how the brain actually works. RSVP-based, word-by-word presentation eliminates saccadic overhead and reduces cognitive load — making any text feel natural to read.", url: "pageburn.site" },
            { name: "SKILLS", tag: "AI System Prompts", status: "Live", desc: "Engineered system prompts packaged as .MD files. Built in production, not theory. Load into any AI for consistent, professional-grade output on demand.", url: null },
            { name: "BRUBRI", tag: "Execution System", status: "Beta", desc: "Mobile-first task execution system built for neurodivergent brains. Microtask fragmentation, neuro-state tracking, and multiple focus modes designed around ADHD and AuDHD execution patterns.", url: null },
            { name: "NAOMI", tag: "Connection Layer", status: "Soon", desc: "A connection layer built to remain. Not built to assist. Details to follow.", url: null },
          ].map(({ name, tag, status, desc, url }) => (
            <div key={name} className={styles.productCard}>
              <div className={styles.productCardHeader}>
                <div>
                  <p className={styles.productTag}>{tag}</p>
                  <h3 className={styles.productName}>{name}</h3>
                </div>
                <span className={`${styles.productStatus} ${status === "Live" ? styles.statusLive : ""}`}>{status}</span>
              </div>
              <p className={styles.productDesc}>{desc}</p>
              {url && (
                <a href={`https://${url}`} target="_blank" rel="noopener" className={styles.productUrl}>{url} ↗</a>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <section className={styles.section}>
        <p className={styles.sectionLabel}>Brand Assets</p>
        <div className={styles.assetsGrid}>
          {["Logo — Dark", "Logo — Light", "Wordmark", "Icon Mark"].map((asset) => (
            <div key={asset} className={styles.assetCard}>
              <div className={styles.assetPreview}>
                <span className={styles.assetPreviewText}>PD</span>
              </div>
              <div className={styles.assetMeta}>
                <span className={styles.assetName}>{asset}</span>
                <span className={styles.assetRequest}>Request via email</span>
              </div>
            </div>
          ))}
        </div>
        <p className={styles.assetsNote}>
          Brand assets available upon request. Contact{" "}
          <a href="mailto:global@prodigiumdynamics.space">global@prodigiumdynamics.space</a>
        </p>
      </section>

      <div className="rule" />

      <section className={styles.ctaSection}>
        <div className="reveal">
          <p className={styles.ctaLabel}>Media Inquiries</p>
          <h2 className={styles.ctaTitle}>Writing about us?</h2>
          <p className={styles.ctaDesc}>
            For interviews, fact-checking, or additional information, reach us directly.
            We respond to press inquiries within 48 hours.
          </p>
          <Link to="/contact" className="btn-primary">Contact Press Team →</Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
